"use client";

// Returns a copy of baseBuffer with the window of segmentDurationSeconds starting at segmentStartSeconds overwritten by segmentBuffer, equal-power crossfading crossfadeSeconds of audio at each boundary so the seam is inaudible. The fade is skipped where the window meets a clip edge, and the overwrite is clamped to both the window and the base length — the intended window, not the returned byte count, bounds what gets replaced, so an over-long segment can never overwrite base audio past it. A mono segment is fanned out across every base channel.
export const spliceAudioBuffer = async ({
  baseBuffer,
  segmentBuffer,
  segmentStartSeconds,
  segmentDurationSeconds,
  crossfadeSeconds,
}: {
  baseBuffer: AudioBuffer;
  segmentBuffer: AudioBuffer;
  segmentStartSeconds: number;
  segmentDurationSeconds: number;
  crossfadeSeconds: number;
}): Promise<AudioBuffer> => {
  // Both buffers decode through the same context rate today; a mismatch would silently resample (pitch/length-shift) the spliced window, so fail loudly instead — the runner's fallback cascade turns this into a full render.
  if (segmentBuffer.sampleRate !== baseBuffer.sampleRate) {
    throw new Error(
      `Segment sample rate ${segmentBuffer.sampleRate} does not match base sample rate ${baseBuffer.sampleRate}`
    );
  }
  const sampleRate = baseBuffer.sampleRate;
  const startSample = Math.round(segmentStartSeconds * sampleRate);
  const windowSamples = Math.min(
    Math.round(segmentDurationSeconds * sampleRate),
    baseBuffer.length - startSample
  );
  const segmentLength = Math.min(segmentBuffer.length, windowSamples);
  const fadeSamples = Math.min(
    Math.round(crossfadeSeconds * sampleRate),
    Math.floor(segmentLength / 2)
  );
  const segmentTouchesStart = startSample <= 0;
  // Derived from the intended window, not the decoded segment length, so a malformed response can't suppress the trailing crossfade.
  const segmentTouchesEnd = startSample + windowSamples >= baseBuffer.length;

  const audioContext = new AudioContext();
  try {
    const output = audioContext.createBuffer(
      baseBuffer.numberOfChannels,
      baseBuffer.length,
      sampleRate
    );

    for (let channel = 0; channel < baseBuffer.numberOfChannels; channel++) {
      const baseData = baseBuffer.getChannelData(channel);
      const outputData = output.getChannelData(channel);
      outputData.set(baseData);

      const segmentChannel =
        channel < segmentBuffer.numberOfChannels ? channel : 0;
      const segmentData = segmentBuffer.getChannelData(segmentChannel);

      for (let i = 0; i < segmentLength; i++) {
        const outputIndex = startSample + i;
        const inLeadFade = !segmentTouchesStart && i < fadeSamples;
        const inTrailFade =
          !segmentTouchesEnd && i >= segmentLength - fadeSamples;

        if (inLeadFade) {
          const progress = (i + 1) / (fadeSamples + 1);
          const segmentGain = Math.sin((Math.PI / 2) * progress);
          const baseGain = Math.cos((Math.PI / 2) * progress);
          outputData[outputIndex] =
            baseData[outputIndex] * baseGain + segmentData[i] * segmentGain;
        } else if (inTrailFade) {
          const progress = (segmentLength - i) / (fadeSamples + 1);
          const segmentGain = Math.sin((Math.PI / 2) * progress);
          const baseGain = Math.cos((Math.PI / 2) * progress);
          outputData[outputIndex] =
            baseData[outputIndex] * baseGain + segmentData[i] * segmentGain;
        } else {
          outputData[outputIndex] = segmentData[i];
        }
      }
    }

    return output;
  } finally {
    await audioContext.close();
  }
};
